// Shared primitives used across admin tabs: KPI cards, ECharts wrapper,
// table shells, source/status badges, formatters. Pulled into one file
// so each tab module only imports its data + tab-specific markup.

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import type { AgGridReactProps } from "ag-grid-react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";

// ---------------------------------------------------------------------------
// AdminGrid — thin ag-grid wrapper used everywhere a real data table
// renders. Centralizes the theme + default column behavior so Admin and
// Training Center grids stay visually identical.
//
// `domLayout="autoHeight"` is the default — the grid grows to fit its
// rows, which is what you almost always want for ≤100-row admin tables.
// For longer lists (Sessions, Activity, Evals), pass an explicit
// `height` and the wrapper switches to fixed-height + internal scroll
// (ag-grid handles row virtualization).
// ---------------------------------------------------------------------------

interface AdminGridProps<T> extends Omit<AgGridReactProps<T>, "domLayout"> {
  height?: number;
}

export function AdminGrid<T>({ height, ...props }: AdminGridProps<T>) {
  const containerStyle = height ? { width: "100%", height } : { width: "100%" };
  return (
    <div className="ag-theme-quartz rounded-[14px] overflow-hidden border border-line" style={containerStyle}>
      <AgGridReact<T>
        domLayout={height ? "normal" : "autoHeight"}
        defaultColDef={{ sortable: true, filter: true, resizable: true }}
        {...props}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI card — small dashboard tile (value + label)
// ---------------------------------------------------------------------------

interface KpiCardProps {
  label: string;
  value?: string | number;
  children?: React.ReactNode;
}

export function KpiCard({ label, value, children }: KpiCardProps) {
  return (
    <div className="rounded-[12px] border border-line bg-white p-4 min-h-[88px] flex flex-col justify-between">
      <div className="text-[11px] uppercase tracking-wide text-neutral-500">{label}</div>
      {children ? (
        <div className="mt-2">{children}</div>
      ) : (
        <div className="text-2xl font-semibold text-neutral-900 mt-1">{value ?? "—"}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EChart wrapper — uniform sizing, no canvas/ref bookkeeping in tab code
// ---------------------------------------------------------------------------

interface ChartProps {
  option: EChartsOption;
  height?: number | string;
  className?: string;
}

export function Chart({ option, height = 220, className }: ChartProps) {
  return (
    <ReactECharts
      option={option}
      notMerge={true}
      lazyUpdate={true}
      style={{ height, width: "100%" }}
      className={className}
    />
  );
}

// ---------------------------------------------------------------------------
// Loading + error states
// ---------------------------------------------------------------------------

export function LoadingBlock({ label = "Loading…" }: { label?: string }) {
  return <div className="text-sm text-neutral-500 py-8 text-center">{label}</div>;
}

export function ErrorBlock({ error }: { error: Error | string }) {
  const msg = typeof error === "string" ? error : error.message;
  return (
    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      {msg}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Source badge — colours match admin.js's sourceBadge()
// ---------------------------------------------------------------------------

const SOURCE_COLOURS: Record<string, string> = {
  web: "bg-blue-50 text-blue-700",
  mcp: "bg-purple-50 text-purple-700",
  slack: "bg-amber-50 text-amber-700",
  sdk: "bg-emerald-50 text-emerald-700",
  gemini: "bg-sky-50 text-sky-700",
  terminal: "bg-neutral-100 text-neutral-700",
};

export function SourceBadge({ source }: { source: string | null | undefined }) {
  const v = (source || "").toLowerCase();
  const cls = SOURCE_COLOURS[v] || "bg-neutral-100 text-neutral-600";
  return (
    <span className={`inline-block rounded-full text-[11px] font-medium px-2 py-0.5 ${cls}`}>
      {v || "—"}
    </span>
  );
}

export function StatusBadge({ status }: { status: string | null | undefined }) {
  const v = (status || "").toLowerCase();
  const cls =
    v === "active"
      ? "bg-emerald-50 text-emerald-700"
      : v === "inactive"
        ? "bg-neutral-100 text-neutral-600"
        : "bg-amber-50 text-amber-700";
  return (
    <span className={`inline-block rounded-full text-[11px] font-medium px-2 py-0.5 ${cls}`}>
      {v || "—"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatNumber(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString();
}

export function formatPercent(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}

export function formatSeconds(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  return `${(ms / 1000).toFixed(1)} s`;
}

export function formatCurrency(v: number | null | undefined, digits = 4): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `$${v.toFixed(digits)}`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleString();
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

export function formatDuration(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  const min = sec / 60;
  if (min < 60) return `${min.toFixed(1)}m`;
  return `${(min / 60).toFixed(1)}h`;
}

// ---------------------------------------------------------------------------
// Common ECharts builders — horizontal bar (top-N), line trend
// ---------------------------------------------------------------------------

const PALETTE = "#3B82F6";

export function horizontalBarOption(items: Array<{ label: string; value: number }>): EChartsOption {
  const sorted = [...items].sort((a, b) => a.value - b.value);
  return {
    grid: { left: 0, right: 8, top: 0, bottom: 0, containLabel: true },
    xAxis: { type: "value", axisLine: { show: false }, splitLine: { show: false }, axisLabel: { fontSize: 10 } },
    yAxis: {
      type: "category",
      data: sorted.map((d) => d.label),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { fontSize: 10 },
    },
    series: [
      {
        type: "bar",
        data: sorted.map((d) => d.value),
        itemStyle: { color: PALETTE, borderRadius: [0, 3, 3, 0] },
        barMaxWidth: 14,
      },
    ],
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
  };
}

export function lineTrendOption(points: Array<{ day: string; count: number }>): EChartsOption {
  return {
    grid: { left: 32, right: 16, top: 16, bottom: 28 },
    xAxis: {
      type: "category",
      data: points.map((p) => p.day),
      axisLabel: { fontSize: 11, hideOverlap: true },
      axisLine: { lineStyle: { color: "#e5e7eb" } },
      axisTick: { show: false },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: "#f3f4f6" } },
      axisLabel: { fontSize: 11 },
    },
    series: [
      {
        type: "line",
        data: points.map((p) => p.count),
        smooth: true,
        showSymbol: false,
        lineStyle: { color: PALETTE, width: 2 },
        areaStyle: { color: "rgba(59, 130, 246, 0.10)" },
      },
    ],
    tooltip: { trigger: "axis" },
  };
}
